import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.samuyjoy.website/api/v1';
export const BASE_BACKEND_URL = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const getImageUrl = (url?: string | null): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  return `${BASE_BACKEND_URL}${cleanPath}`;
};

export interface Token {
  access_token: string;
  token_type: string;
}

export interface User {
  id: number;
  telegram_id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  balance: number;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
}

export interface PropertyOwner {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  phone?: string;
}

export interface PropertyImage {
  id: number;
  property_id: number;
  image_url: string;
  is_primary: boolean;
  order: number;
  created_at: string;
}

export interface Property {
  id: number;
  title: string;
  description: string;
  property_type: 'apartment' | 'house';
  price: number;
  area: number;
  rooms?: number;
  bathrooms?: number;
  floor?: number;
  total_floors?: number;
  address: string;
  city: string;
  latitude?: number;
  longitude?: number;
  status: 'pending' | 'active' | 'sold' | 'rejected';
  is_paid: boolean;
  owner_id: number;
  owner?: PropertyOwner;
  created_at: string;
  updated_at: string;
  published_at?: string;
  expires_at?: string;
  admin_notes?: string;
  images: PropertyImage[];
}

export interface PropertyFilter {
  property_type?: 'apartment' | 'house';
  city?: string;
  min_price?: number;
  max_price?: number;
  min_area?: number;
  max_area?: number;
  rooms?: number;
  min_rooms?: number;
  max_rooms?: number;
  status?: 'pending' | 'active' | 'sold' | 'rejected';
  page?: number;
  page_size?: number;
}

export interface Pricing {
  id: number;
  name: string;
  description?: string;
  price: number;
  duration_days: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const authAPI = {
  register: (data: any) => api.post('/auth/register', data),
  login: (telegram_id: string, password: string) =>
    api.post('/auth/login', null, { params: { telegram_id, password } }),
  telegramAuth: (data: any) => api.post('/auth/telegram-auth', data),
  adminLogin: (username: string, password: string) =>
    api.post<Token>('/auth/admin-login', { username, password }),
  getCurrentUser: () => api.get<User>('/auth/me'),
  updateCurrentUser: (data: any) => api.put<User>('/auth/me', data),
};

export const propertiesAPI = {
  list: (filters?: PropertyFilter) =>
    api.get<Property[]>('/properties', { params: filters }),
  get: (id: number) => api.get<Property>(`/properties/${id}`),
  create: (data: any) => api.post<Property>('/properties', data),
  update: (id: number, data: any) => api.put<Property>(`/properties/${id}`, data),
  delete: (id: number) => api.delete(`/properties/${id}`),
  getMyProperties: () => api.get<Property[]>('/properties/my/properties'),
  getListingPrice: () => api.get<{ listing_price: number; currency: string }>('/properties/config/listing-price'),
  uploadImage: (id: number, file: File, is_primary: boolean = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_primary', String(is_primary));
    return api.post(`/properties/${id}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadImages: (id: number, formData: FormData) => {
    return api.post(`/properties/${id}/images/bulk`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const adminAPI = {
  listPricing: () => api.get<Pricing[]>('/admin/pricing/all'),
  createPricing: (data: any) => api.post<Pricing>('/admin/pricing', data),
  updatePricing: (id: number, data: any) => api.put<Pricing>(`/admin/pricing/${id}`, data),
  deletePricing: (id: number) => api.delete(`/admin/pricing/${id}`),
  listUsers: (skip: number = 0, limit: number = 100) =>
    api.get<User[]>('/auth/users', { params: { skip, limit } }),
  deleteUser: (id: number) => api.delete(`/auth/users/${id}`),

  // Categories
  listCategories: () => api.get('/admin/categories'),
  createCategory: (data: any) => api.post('/admin/categories', data),
  updateCategory: (id: number, data: any) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id: number) => api.delete(`/admin/categories/${id}`),

  // Statistics
  getStatistics: () => api.get('/admin/statistics'),

  // Moderation
  getPendingProperties: () => api.get<Property[]>('/admin/properties/pending'),
  approveProperty: (id: number) => api.post(`/admin/properties/${id}/approve`),
  rejectProperty: (id: number, reason: string) => api.post(`/admin/properties/${id}/reject`, null, { params: { reason } }),
  getAllProperties: (skip: number = 0, limit: number = 50) =>
    api.get('/admin/properties/all', { params: { skip, limit } }),

  // Settings
  getListingPrice: () => api.get<{ listing_price: number; currency: string }>('/admin/settings/listing-price'),
  setListingPrice: (price: number) => api.post('/admin/settings/listing-price', { price }),
};

export const pricingAPI = {
  list: () => api.get<Pricing[]>('/admin/pricing'),
  get: (id: number) => api.get<Pricing>(`/admin/pricing/${id}`),
};

export const balanceAPI = {
  getBalance: () => api.get<{ balance: number; currency: string }>('/auth/me/balance'),
  requestTopup: (amount: number) => api.post('/auth/me/topup-request', { amount }),
  adminTopup: (userId: number, amount: number) =>
    api.post(`/auth/admin/topup/${userId}`, { amount }),
};

export default api;
